/**
 * Windows sign-in certificates — Zod schemas, the single source of truth for the wire shapes.
 *
 * Transcribed from the Go handler (backend/internal/pki/issuance/admin_handler.go), not guessed:
 *
 *   GET  /admin/logon-certificates/readiness    → Readiness
 *   GET  /admin/logon-certificates/revocations  → { revocations: [...] }
 *   POST /admin/logon-certificates/revocations  → 204 | 404 unknown_serial | 409 already_revoked
 *
 * ── WHY THIS SURFACE EXISTS AT ALL ─────────────────────────────────────────
 *
 * The feature only works for accounts carrying three Active Directory attributes, all of which
 * arrive from the directory connector's attribute mapping. A tenant can be fully configured,
 * syncing cleanly, holding a minted certificate authority — and still unable to sign anybody in,
 * because one attribute was never mapped.
 *
 * Without a page that says so, that is discovered ONE PERSON AT A TIME AT THE WINDOWS SIGN-IN
 * SCREEN, by people who cannot diagnose it. So the server counts, and this surface reports the
 * count and the fix.
 *
 * ── WHAT THE API DELIBERATELY DOES NOT RETURN ──────────────────────────────
 *
 * There is no issuance history. The certificate ledger exists to answer "did we issue this serial?"
 * under an audit or an incident, and a browsable per-person feed would turn a deny-by-default
 * evidence table into a routine console query over who signed in when. That belongs in Audit, with
 * the controls Audit already has. Do not add a list view here without moving that decision first.
 */

import { z } from "zod";

/** The certificate authority, or its absence. */
export const caReadinessSchema = z.object({
  present: z.boolean(),
  generation: z.number().int().optional(),
  status: z.string().optional(),
  /** The Active Directory forest this authority was published into. */
  forest: z.string().optional(),
  not_after: z.string().optional(),
  /**
   * Inside the last 90 days. Renewing means publishing a new authority into the customer's
   * Active Directory and waiting for replication — someone else's change process, so it is a
   * calendar item rather than an alert.
   */
  expires_soon: z.boolean(),
});

/**
 * Eligibility counts.
 *
 * THE THREE "MISSING" COUNTS OVERLAP — one account can be missing all three — so they must never be
 * summed or subtracted from the total. `eligible` is computed server-side as its own filter for
 * exactly that reason.
 */
export const accountReadinessSchema = z.object({
  active: z.number().int(),
  eligible: z.number().int(),
  missing_upn: z.number().int(),
  missing_sid: z.number().int(),
  missing_dn: z.number().int(),
  malformed_identity: z.number().int(),
  // Accounts holding no passkey whose hardware could satisfy Windows sign-in. A DIFFERENT question
  // from the three above: those are answered in the directory connector's attribute mapping, this one
  // by getting people to enroll on their workstation.
  no_device_bound_credential: z.number().int(),
});

/** One certificate authority in a forest's hierarchy, as an operator needs to see it. */
export const authoritySchema = z.object({
  role: z.string(),
  generation: z.number().int(),
  status: z.string(),
  /** Whether this deployment can sign with it. A root that is genuinely offline is the production shape. */
  key_held: z.boolean(),
  not_after: z.string(),
  expires_soon: z.boolean(),
  /**
   * What every Windows certificate surface displays. The operator compares it character by character
   * against certlm.msc or NTAuth, which is the only way to confirm the certificate that landed there is
   * the one we sent.
   */
  sha1_thumbprint: z.string().optional(),
  /**
   * When an administrator ATTESTED that this issuing CA reached its forest's NTAuth.
   *
   * ABSENT IS THE NORMAL STATE between minting and the customer's change window — often weeks — not a
   * fault. But it IS the difference between a minted authority and a working sign-in, which is why the
   * card shows it rather than leaving it to be inferred.
   *
   * The surface says "recorded by", never "verified": PureID cannot read the customer's NTAuth and must
   * not imply it can.
   */
  ntauth_published_at: z.string().optional(),
  ntauth_published_by: z.string().optional(),
  ntauth_reference: z.string().optional(),
  /**
   * The offline root's revocation list. Set on a root only.
   *
   * ITS OWN DEADLINE, AND A LONGER WARNING THAN THE AUTHORITY'S. Only the offline root key can re-sign it,
   * which means a witnessed ceremony booked through someone else's calendar — so the server warns at 180
   * days rather than the 90 it uses for the authority itself. If it lapses, every sign-in in that forest
   * fails.
   */
  root_crl_next_update: z.string().optional(),
  root_crl_due: z.boolean().optional(),
  root_crl_missing: z.boolean().optional(),

  /**
   * ── WHERE THIS CERTIFICATE CAME FROM ────────────────────────────
   *
   * "minted" — PureID generated the root and the issuing CA. "imported" — the customer's own authority
   * signed it and PureID holds only the issuing key. Transcribed from AuthorityReadiness.Origin, which
   * carries no `omitempty`, so it is always sent; defaulted rather than required so a console ahead of
   * its backend degrades to "no chip" instead of failing the whole readiness parse.
   *
   * ONE ROW SHAPE, NOT TWO SUBSYSTEMS (B1). Everything else on this object means the same thing under
   * both origins, which is why this is a field rather than a second schema.
   */
  origin: z.string().default(""),
  /**
   * Who signed this certificate. For a minted issuing CA that is PureID's own root — already on the
   * card as the root row — so the card shows this line only for an imported authority, where it names
   * the customer's authority and is the fact the line exists for.
   */
  issuer_subject: z.string().optional(),
  /**
   * The import attestation. "supplied" means a copy of the key existed outside PureID before import and
   * the customer was told to destroy it; "generated" means it never did (B9 — provenance is exactly what
   * this field records, and the surface must not imply one is better protected than the other).
   */
  key_source: z.string().optional(),
  imported_at: z.string().optional(),
  imported_by: z.string().optional(),
  import_reference: z.string().optional(),
  /**
   * The certificate is its own issuer, so PureID holds a ROOT key (B6). Permanent, like the software-root
   * finding it mirrors: there is nothing above this certificate that could revoke it.
   */
  self_signed: z.boolean().optional(),
  /**
   * When retire erased the private half (B10). Absent while the key is held — and the difference between
   * "offline" (a root nobody can sign with today) and "destroyed" (gone), which is the answer a customer
   * withdrawing their certificate is owed.
   */
  key_destroyed_at: z.string().optional(),
});

/** A directory connection, so a blocker can link to where the attribute mapping actually lives. */
export const directoryRefSchema = z.object({ id: z.string(), name: z.string() });

/**
 * A blocker: the server's sentence, plus the structure needed to act on it.
 *
 * THE TEXT IS STILL RENDERED VERBATIM. What the envelope adds is a link target — a string cannot carry
 * one, and the fix for a missing attribute lives on a specific directory connection.
 */
export const blockerSchema = z.object({
  code: z.string(),
  text: z.string(),
  /** Empty on a tenant-wide blocker, which is how the page knows not to pin it to a forest's card. */
  forest: z.string().optional(),
  directory_ids: z.array(z.string()).default([]),
});

/** One forest's hierarchy and the accounts it can actually certify. */
export const forestReadinessSchema = z.object({
  forest_root_dn: z.string(),
  forest_dns: z.string(),
  authorities: z.array(authoritySchema).default([]),
  /** The domains recorded against this authority. Empty is serious: it can then issue to nobody. */
  domains: z.array(z.string()).default([]),
  accounts: accountReadinessSchema,
  directories: z.array(directoryRefSchema).default([]),
  /**
   * Set when this hierarchy could not be read. Its numbers are then ABSENT rather than zero, and the card
   * must show that rather than rendering zeros — a zero is a claim, and "0 eligible" reads as a broken
   * directory rather than as a failed read.
   */
  read_error: z.string().optional(),
  blockers: z.array(blockerSchema).default([]),
  /**
   * The forest's shape, taken server-side from the active issuing authority (or the newest when none is
   * active) so a card mid-rotation still has an answer. The card derives its chip from the authority row
   * for the same reason the "Issued by" line and the self-signed chip do — one row is what all three
   * describe — and falls back to this when the forest carries no authority at all.
   */
  origin: z.string().default(""),
});

/** A domain holding accounts that no authority covers. */
export const uncoveredDomainSchema = z.object({
  domain_dn: z.string(),
  accounts: z.number().int(),
});

/**
 * Accounts belonging to no forest. Its own section rather than a per-forest tile, because an account with
 * no directory DN cannot be attributed to a hierarchy at all — that count is structurally zero on every
 * card — and its fix is a connector's attribute mapping rather than a forest.
 */
export const unplacedSchema = z.object({
  no_dn: z.number().int(),
  /** A DIFFERENT FIX from no_dn: malformed in Active Directory rather than never mapped here. */
  unreadable_dn: z.number().int(),
  no_covered_domain: z.number().int(),
  domains: z.array(uncoveredDomainSchema).default([]),
  directory_ids: z.array(z.string()).default([]),
});

export const readinessSchema = z.object({
  ca: caReadinessSchema,
  accounts: accountReadinessSchema,
  /**
   * One entry per Active Directory forest this tenant serves.
   *
   * THE PAGE READS THIS, and `ca`/`accounts` above are the single-CA fields the server still returns so
   * the API and console changes could land separately. They describe the EARLIEST-expiring hierarchy,
   * which is honest for a one-forest tenant and misleading for any other — so nothing below should use
   * them to compute a verdict.
   */
  forests: z.array(forestReadinessSchema).default([]),
  unplaced: unplacedSchema,
  /**
   * Tenant-wide blockers in structured form — the ones carrying a directory id to link to.
   *
   * Distinct from `blockers` below, which is the legacy string list. A string cannot carry a link, and the
   * fix for a missing attribute lives on a specific directory connection.
   */
  tenant_blockers: z.array(blockerSchema).default([]),
  /** Forests whose data could not be read, so the page can say "2 of 3 checked" instead of blanking. */
  unreadable: z.array(z.string()).default([]),
  /**
   * Plain-language sentences, already written by the server. Rendered verbatim rather than
   * re-derived here: the backend knows which attribute is missing and where the fix is, and two
   * places composing the same message is two places for them to disagree.
   */
  blockers: z.array(z.string()).default([]),
});

export const revocationSchema = z.object({
  serial: z.string(),
  /** Empty when the ledger row has aged out of the join. */
  upn: z.string().default(""),
  reason: z.string(),
  reason_code: z.number().int(),
  revoked_at: z.string(),
  revoked_by: z.string(),
  note: z.string().default(""),
  /**
   * The certificate has passed its own expiry, so it no longer appears on the published revocation
   * list. NOT a failure — surfaced so an operator whose revocation "disappeared" can see why.
   */
  expired: z.boolean(),
});

export const revocationsResponseSchema = z.object({
  revocations: z.array(revocationSchema).default([]),
});

/**
 * The four RFC 5280 reasons the backend accepts.
 *
 * Deliberately not the full enumeration: an operator offered ten mostly-inapplicable codes picks the
 * wrong one, and the code travels to every domain controller that reads the list.
 */
export const REVOCATION_REASONS = [
  { value: 1, label: "Key compromise", hint: "The private key is believed to be stolen." },
  { value: 4, label: "Superseded", hint: "Replaced by a newer certificate." },
  { value: 5, label: "Cessation of operation", hint: "The account or authority is being wound down." },
  { value: 0, label: "Unspecified", hint: "No reason recorded." },
] as const;

/**
 * The honest default. The form must NOT pre-select REVOCATION_REASONS[0] ("Key compromise", RFC 5280
 * code 1): an operator who revokes without opening the dropdown would then assert a stolen key they
 * never claimed, and that alarming reason travels to every domain controller and into the audit record.
 * "Unspecified" (code 0) is the value the operator has actually chosen when they have chosen nothing.
 */
export const DEFAULT_REVOCATION_REASON = 0;

export const revokeInputSchema = z.object({
  /**
   * Accepted in the forms operators actually paste — the server normalises uppercase, spacing,
   * colons and 0x prefixes. Validating strictly here would reject a correct serial mid-incident.
   */
  serial: z.string().trim().min(1, "Enter the certificate serial."),
  reason: z.number().int(),
  note: z.string().trim().max(500).optional(),
});

/**
 * The trust-distribution packet for one forest.
 *
 * THREE ROLES, NOT TWO. "root" and "issuing" for a minted hierarchy; for an imported one the packet
 * carries the issuing certificate plus zero or more "issuer_chain" entries — the customer's own chain,
 * included for reference because the page's first instruction is to CONFIRM their root is already
 * trusted rather than to distribute one (B2/B3). Chain entries carry no generation and no filename,
 * because there is nothing for the AD team to save: those certificates are already on their machines.
 */
export const trustCertificateSchema = z.object({
  role: z.string(),
  generation: z.number().int(),
  subject: z.string(),
  sha1_thumbprint: z.string(),
  not_after: z.string(),
  /** Names the forest, so a certificate copied into the wrong NTAuth is at least visibly wrong. */
  filename: z.string(),
  pem: z.string(),
});

/**
 * ── THE CERTIFICATE PROFILE VERSION ────────────────────────────────────────
 *
 *   GET /logon-certificates/profile  → CertificateProfile
 *   PUT /logon-certificates/profile  → { version } | 400 unsupported_profile_version
 *                                                  | 409 crl_cannot_cover_profile
 *
 * WHAT THE SETTING ACTUALLY IS. It bounds how long a certificate this organisation issues may last,
 * which is how long a machine keeps signing people in with PureID unreachable. It is a PERMISSION and
 * never a preference: the default lifetime stays one hour under every version, so raising it changes
 * nothing until an offline window is written down on the workstations page.
 *
 * THREE FACTS ARRIVE SEPARATELY AND MAY BE UNKNOWN INDEPENDENTLY, which is why `known` and
 * `live_known` exist rather than the numbers simply defaulting. A deployment that could not read the
 * stored selection must not be rendered as "version 1" — that would describe an organisation running
 * a 72-hour ceiling as one running a 24-hour ceiling, on the screen an operator opens to check
 * exactly that.
 */

/** One version this deployment implements, with what it costs and whether it can be chosen. */
export const profileVersionSchema = z.object({
  version: z.number().int(),
  /** The ceiling: the longest certificate this version permits. */
  max_validity_hours: z.number(),
  /** What an organisation that has written down no offline window gets. One hour in every version. */
  default_validity_hours: z.number(),
  /**
   * How stale a revocation decision may be for a certificate this version issued. 0 means the version
   * states none, which only a ceiling low enough not to need one may do.
   */
  max_revocation_latency_hours: z.number(),
  /**
   * False when this deployment's revocation list cannot cover the version. The control must not be
   * offered — the write would be refused, and the reason is a change to the deployment rather than
   * anything an operator can do from here.
   */
  selectable: z.boolean(),
  /** The server's own sentence. Rendered verbatim: the remedies are configuration, and one is wrong. */
  unselectable_reason: z.string().default(""),
});

/**
 * One version's outstanding certificates — the drain.
 *
 * THE FACT THE WARNING RESTS ON. A change takes one full validity window to take effect in BOTH
 * directions: lowering the ceiling does not shorten a certificate already issued. This says how many
 * are still out there and when the last of them stops working, so the console states a date rather
 * than implying immediacy.
 */
export const liveProfileSchema = z.object({
  profile_version: z.number().int(),
  count: z.number().int(),
  latest_not_after: z.string(),
});

/**
 * The requested offline window beside the one the ceiling actually permits.
 *
 * `requested_minutes` is the TENANT layer of the offline window — the server's collapse of
 * policy.offline.windowMinutes over policy.offline.windowHours, never either path alone. Machines and
 * groups may widen it and those values live on the workstations page; the tenant value is the one this
 * page can name, and it is also the one that is supposed to stay narrow.
 */
export const profileWindowSchema = z.object({
  /** Distinguishes "asked for nothing" from "asked for zero". Unset is the normal state. */
  set: z.boolean(),
  /**
   * BOTH NUMBERS ARE MINUTES, and that is not cosmetic. The window has minute resolution
   * (policy.offline.windowMinutes wins over policy.offline.windowHours), so an hours-only pair
   * rendered a fifteen-minute window as "0 hours requested" — a zero, on the one screen an operator
   * opens to read what is actually being issued, is a claim rather than a rounding.
   */
  requested_minutes: z.number().int(),
  effective_minutes: z.number().int(),
  /**
   * The ceiling decided, not merely that the two numbers differ. An unset window resolves to the
   * one-hour default, which is a preference rather than a limit — flagging that as a clamp would put
   * a warning on every organisation that has changed nothing.
   *
   * ONE DIRECTION ONLY. A request under the profile's five-minute floor is raised to it, and the
   * server does NOT report that as a clamp: the remedy for a clamp is the ceiling control, and the
   * ceiling had nothing to do with it.
   */
  clamped: z.boolean(),
});

export const certificateProfileSchema = z.object({
  /** Absent when `known` is false — never rendered as a version in that case. */
  version: z.number().int().optional(),
  known: z.boolean(),
  versions: z.array(profileVersionSchema).default([]),
  window: profileWindowSchema.optional(),
  live: z.array(liveProfileSchema).default([]),
  /** False means this deployment could not look — a different statement from an empty fleet. */
  live_known: z.boolean(),
  /** False when the deployment has no writer wired, so the page shows state without a dead control. */
  manageable: z.boolean(),
});

export const setCertificateProfileResultSchema = z.object({
  version: z.number().int(),
  previous_version: z.number().int(),
  /** False for a write that restated the current selection — nothing moved, and nothing was audited. */
  changed: z.boolean(),
});

export const trustPacketSchema = z.object({
  forest_root_dn: z.string(),
  forest_dns: z.string(),
  domains: z.array(z.string()).default([]),
  certificates: z.array(trustCertificateSchema).default([]),
  addresses: z.object({
    leaf_crl: z.string(),
    leaf_aia: z.string(),
    issuing_crl: z.string(),
    issuing_aia: z.string(),
  }),
  pki_host: z.string(),
  /**
   * The copyable block for a change ticket — composed by the same server-side code `ca init` prints and
   * `ca export` writes, so the customer and our support engineer cannot end up reading different
   * instructions for the same forest. Copying this is the page's PRIMARY action.
   */
  steps: z.string(),
  /** "minted" or "imported". `steps` is already origin-aware; this is what the page's own copy keys on. */
  origin: z.string().default(""),
  /**
   * Present only while a replacement is waiting to be promoted (B11), and it changes what this page is
   * FOR: the certificate the AD team still has to publish is the incoming generation, which arrives in
   * `certificates` under role "incoming". Handing them the active one during a rollover is handing them
   * a file already in NTAuth — publishing it again achieves nothing and looks like it worked.
   */
  rollover: z
    .object({
      active_generation: z.number().int(),
      incoming_generation: z.number().int(),
      /** Whether the incoming generation's publication is recorded. Promotion is gated on it. */
      ntauth_recorded: z.boolean(),
    })
    .optional(),
});

export type Authority = z.infer<typeof authoritySchema>;
export type Blocker = z.infer<typeof blockerSchema>;
export type ForestReadiness = z.infer<typeof forestReadinessSchema>;
export type TrustPacket = z.infer<typeof trustPacketSchema>;
export type TrustCertificate = z.infer<typeof trustCertificateSchema>;
export type Readiness = z.infer<typeof readinessSchema>;
export type Revocation = z.infer<typeof revocationSchema>;
export type RevokeInput = z.infer<typeof revokeInputSchema>;

/**
 * ── The mint ceremony: preview, then create ─────────────────────────────────
 *
 * Transcribed from backend/internal/pki/issuance/ceremony_handler.go:
 *
 *   POST .../logon-certificates/authorities/preview  -> CeremonyPreview
 *   POST .../logon-certificates/authorities          -> CeremonyCreated | 409 | 422
 *
 * TWO REQUESTS BECAUSE THE GATES ONLY WORK IF SOMEBODY READS THE CENSUS. The create call must echo
 * `census_digest` from the preview, and the server recomputes it — so a directory that moved between
 * the two forces a fresh preview rather than minting against a stale picture. The server is explicit
 * that this is not an anti-automation control: it proves the census was CURRENT and fetched. Making a
 * human read is this UI's job, which is why the wizard renders the census as a step rather than a
 * detail behind a disclosure triangle.
 */

/** One domain the directory holds, and whether this ceremony will cover it. */
export const censusDomainSchema = z.object({
  domain_dn: z.string(),
  accounts: z.number().int(),
  covered: z.boolean(),
});

/**
 * A gate that will refuse.
 *
 * `acknowledgeable` is the field that decides the UI: false means this caller cannot proceed by
 * explaining themselves, so no reason box is offered. The cross-tenant gate is always false here —
 * it is a decision about a relationship between two customers that neither can make alone — and the
 * server deliberately reports a COUNT and never which organisations, so there is nothing to render
 * beyond what it sends.
 */
export const ceremonyGateSchema = z.object({
  code: z.string(),
  text: z.string(),
  acknowledgeable: z.boolean(),
});

export const ceremonyPreviewSchema = z.object({
  forest: z.string(),
  forest_dns: z.string(),
  census_digest: z.string(),

  active_accounts: z.number().int(),
  with_readable_dn: z.number().int(),
  without_dn: z.number().int(),
  unreadable_dn: z.number().int(),
  domains: z.array(censusDomainSchema).default([]),
  /** The domains this ceremony will record. Anything outside is refused until someone adds it. */
  will_cover: z.array(z.string()).default([]),
  outside: z.array(z.string()).default([]),
  warning: z.string().default(""),
  blockers: z.array(ceremonyGateSchema).default([]),
  pki_host: z.string(),
  addresses: z.object({
    leaf_crl: z.string(),
    leaf_aia: z.string(),
    issuing_crl: z.string(),
    issuing_aia: z.string(),
  }),
});

export const ceremonyCreatedSchema = z.object({
  forest: z.string(),
  forest_dns: z.string(),
  generation: z.number().int(),
  domains: z.array(z.string()).default([]),
  /**
   * The handover packet, composed server-side by the same code `ca init` prints and `ca export`
   * writes. Returned with the creation so the operator's next step needs no second request.
   *
   * NOT CACHED. It is not secret — a CA certificate is public — but it belongs to one ceremony, and a
   * cached copy would outlive the moment it describes.
   */
  steps: z.string(),
});

export type CensusDomain = z.infer<typeof censusDomainSchema>;
export type CeremonyGate = z.infer<typeof ceremonyGateSchema>;
export type CeremonyPreview = z.infer<typeof ceremonyPreviewSchema>;
export type CeremonyCreated = z.infer<typeof ceremonyCreatedSchema>;

/**
 * The forest DN a operator types.
 *
 * VALIDATED LOOSELY HERE ON PURPOSE. The server's ValidateForestRootDN is the authority — it enforces
 * DC-components-only and the DNS-label charset, and it is the check that runs before any key exists.
 * Duplicating those rules in the browser would be a second copy to drift; what this catches is an
 * empty box and an obvious non-DN, so the operator is not sent to the server to be told so.
 */
export const forestInputSchema = z.object({
  forest: z
    .string()
    .trim()
    .min(1, "Enter the forest root distinguished name.")
    .refine((v) => /dc=/i.test(v), "A forest root looks like DC=example,DC=internal."),
});
export type ForestInput = z.infer<typeof forestInputSchema>;

/**
 * ── The import ceremony: the customer's own certificate becomes the authority ─
 *
 * Transcribed from the slice-2 wire contract, which both this console and the handler are built
 * against:
 *
 *   POST .../logon-certificates/authorities/import/preview  -> ImportPreview
 *   POST .../logon-certificates/authorities/import          -> ImportedAuthority | 409 | 422
 *
 * SAME TWO-REQUEST SHAPE AS THE MINT, FOR ONE MORE REASON (B8). The mint splits so a human reads the
 * census before a trust anchor exists. Import splits for that AND because the preview is PUBLIC
 * material only — certificate, chain, forest — so every gate and every validation check can be run,
 * and every refusal delivered, before the private key has travelled anywhere. The key then travels
 * exactly once, with the digest of the census AND the digest of the certificate that was previewed, so
 * the server can prove the operator confirmed the thing they were shown. A key that had to be uploaded
 * twice would be a key kept between two requests, on the client or the server, and neither is
 * acceptable.
 */

/**
 * One validation check, as the operator sees it.
 *
 * EVERY CHECK IS SENT AND EVERY CHECK IS RENDERED, passing ones included (design record, stage 3 review,
 * UX engineer note): a list of only failures reads as "the rest was not looked at", on the screen whose
 * whole job is to say what was looked at.
 *
 * `pending` is its own level rather than an omission. It means the check needs the private key and this
 * was a preview of public material — reported so the operator knows the check exists and has NOT been
 * passed.
 *
 * THE ENUM IS STRICT ON PURPOSE. A level this console does not know is a version skew, and
 * describeCeremonyError already names that failure — which is a better outcome than rendering an
 * unknown verdict as though it were understood, on the screen that decides whether a certificate
 * becomes a forest-wide trust anchor.
 */
export const importCheckSchema = z.object({
  /** Stable and machine-readable. The console keys on it; the audit row records it. */
  code: z.string(),
  level: z.enum(["passed", "warning", "refused", "pending"]),
  /** The sentence. For a refusal it names the fix, so it is rendered verbatim. */
  detail: z.string(),
});

/**
 * What the certificate says about itself, as the server read it.
 *
 * READ SERVER-SIDE, NOT IN THE BROWSER. The console could parse the file for display, and it
 * deliberately does not: the server's verdict is the truth, and a client-parsed field must never gate
 * the Continue button (design record, stage 3 review, architect note). One parser, one set of facts.
 */
export const importCertificateFactsSchema = z.object({
  subject: z.string(),
  issuer: z.string(),
  not_before: z.string(),
  not_after: z.string(),
  key_bits: z.number().int(),
  /** In certutil's spaced hex groups — the form the operator compares against certlm.msc. */
  sha1: z.string(),
  sha256: z.string(),
  /** True when the certificate is its own issuer. Import then requires an acknowledgement (B6). */
  self_signed: z.boolean(),
  chain_length: z.number().int(),
  root_subject: z.string().default(""),
  root_sha1: z.string().default(""),
  /**
   * What the certificate ITSELF carries — the customer's publisher, not PureID's. Shown so the operator
   * can see which addresses their domain controllers will fetch for this link of the chain; never
   * mirrored or served by PureID (B3).
   */
  cdp_urls: z.array(z.string()).default([]),
  aia_urls: z.array(z.string()).default([]),
});

/**
 * The rollover a replacement preview describes.
 *
 * THE OUTGOING GENERATION IS NAMED, NOT COUNTED. "generation 1 keeps signing until you promote
 * generation 2" is the sentence that makes make-before-break legible, and the two numbers in it are
 * what the operator puts in their change ticket.
 */
export const importReplacementSchema = z.object({
  outgoing_generation: z.number().int(),
  incoming_generation: z.number().int(),
  /** "minted" or "imported". A minted outgoing generation makes this the migration to your own PKI. */
  outgoing_origin: z.string().default(""),
  outgoing_subject: z.string().default(""),
  /**
   * The coverage the incoming generation inherits AT PROMOTE, not at import. It records none of its own
   * — one authority holds a domain — so this is what will move, and the wizard says so rather than
   * showing it as coverage the new certificate already has.
   */
  outgoing_domains: z.array(z.string()).default([]),
});

/**
 * The import preview.
 *
 * THE CENSUS HALF IS THE MINT PREVIEW'S, FIELD FOR FIELD, because the same gates run in the same order
 * (B7) — the customer having supplied the certificate is not evidence about which forest it should
 * serve. What differs is `addresses`, `certificate`, `checks` and `importable`.
 *
 * `addresses` CARRIES THE LEAF PAIR ONLY. An imported authority publishes no issuing-CA CRL of PureID's
 * — that pair belongs to the customer's PKI and lives inside their certificate — so the two mint-only
 * fields are absent rather than empty, and the frozen-addresses panel renders two lines instead of four.
 *
 * A REFUSED CHECK IS STILL A 200. The wizard renders the checks; only the forest gates and an unparseable
 * certificate are 4xx. `importable` is the server's own verdict — false iff any check refused — and the
 * console disables the button on the checks rather than trusting a single boolean it cannot explain.
 */
export const importPreviewSchema = z.object({
  forest: z.string(),
  forest_dns: z.string(),
  census_digest: z.string(),
  /** Lowercase hex SHA-256 of the certificate DER. Echoed on the import so the server can prove the
   *  operator confirmed the certificate they were shown. */
  certificate_digest: z.string(),

  active_accounts: z.number().int(),
  with_readable_dn: z.number().int(),
  without_dn: z.number().int(),
  unreadable_dn: z.number().int(),
  domains: z.array(censusDomainSchema).default([]),
  will_cover: z.array(z.string()).default([]),
  outside: z.array(z.string()).default([]),
  warning: z.string().default(""),
  blockers: z.array(ceremonyGateSchema).default([]),
  pki_host: z.string(),
  addresses: z.object({
    leaf_crl: z.string(),
    leaf_aia: z.string(),
  }),

  certificate: importCertificateFactsSchema,
  checks: z.array(importCheckSchema).default([]),
  importable: z.boolean(),

  /**
   * Present only on a REPLACEMENT preview (B11), and absent on a first import.
   *
   * OPTIONAL RATHER THAN NULLABLE, because its absence is what tells the wizard which ceremony it is
   * in — a block of zeroes would render "replacing generation 0" on the screen an operator confirms a
   * forest-wide change from.
   */
  replacement: importReplacementSchema.optional(),
});

/**
 * The authority that now exists.
 *
 * `steps` is the same trust packet the mint returns and `ca import` prints — origin-aware server-side,
 * so the console does not compose a second set of instructions for the same forest.
 */
export const importedAuthoritySchema = z.object({
  forest: z.string(),
  forest_dns: z.string(),
  generation: z.number().int(),
  /**
   * "active" on a first import, "verifying" on a replacement — and the difference is the whole of what
   * the done step has to say. A verifying authority is trusted and signing nothing.
   */
  status: z.string().default("active"),
  origin: z.string(),
  key_source: z.string(),
  domains: z.array(z.string()).default([]),
  subject: z.string(),
  issuer: z.string(),
  sha1: z.string(),
  not_after: z.string(),
  self_signed: z.boolean(),
  steps: z.string(),
  /** Present only on a replacement: the generation still signing until this one is promoted. */
  replaces_generation: z.number().int().optional(),
  /** The server's own sentence about what remains. Rendered verbatim; one place composes it. */
  next: z.string().default(""),
});

/**
 * What a promotion changed.
 *
 * `coverage_moved` IS NOT DECORATION. Issuance resolves through recorded coverage, so an authority that
 * inherited none can issue to nobody — a rollover that reports success and locks the forest out. The
 * server says so in `note`; the console keys on the number to decide how loudly.
 */
export const promoteResultSchema = z.object({
  forest: z.string(),
  from_generation: z.number().int(),
  to_generation: z.number().int(),
  ntauth_attested: z.boolean(),
  coverage_moved: z.number().int(),
  note: z.string().default(""),
});

export type ImportCheck = z.infer<typeof importCheckSchema>;
export type ImportCertificateFacts = z.infer<typeof importCertificateFactsSchema>;
export type ImportPreview = z.infer<typeof importPreviewSchema>;
export type ImportReplacement = z.infer<typeof importReplacementSchema>;
export type ImportedAuthority = z.infer<typeof importedAuthoritySchema>;
export type PromoteResult = z.infer<typeof promoteResultSchema>;

/**
 * ── CSR-OUT: PureID generates the key ──────────────────
 *
 *   POST .../logon-certificates/authorities/csr  -> PendingCSR
 *   GET  .../logon-certificates/authorities/csr  -> { pending: PendingCSR[] }
 *
 * WHY THE READ EXISTS. The two halves of this ceremony are separated by the customer's own change
 * process — days, often weeks. Nothing else on this surface remembers across that gap, so without the
 * list an operator returning with a signed certificate could not see that a request is outstanding,
 * could not fetch the request again, and would reach for "generate another" — which supersedes the
 * key their certificate answers and wastes the window they just spent.
 *
 * NOTHING SECRET IS IN THIS SHAPE. `csr_pem` is a public key and a subject with a signature over them.
 * The private half is sealed on the backend and has no path to a response body, which is the property
 * the whole mode exists for.
 */
export const pendingCSRSchema = z.object({
  id: z.string(),
  forest: z.string(),
  forest_dns: z.string(),
  subject: z.string(),
  csr_pem: z.string(),
  /** Names the forest, so two forests' requests cannot be confused once they are files on a desk. */
  filename: z.string(),
  created_at: z.string(),
  created_by: z.string(),
  expires_at: z.string(),
  /** The number the operator is deciding on: whether their change window fits inside what is left. */
  days_left: z.number().int(),
  /**
   * Requests this one displaced. Non-zero is a finding rather than a detail — a CSR their PKI team may
   * already be holding will now be refused.
   */
  superseded: z.number().int().default(0),
});

export const pendingCSRListSchema = z.object({
  pending: z.array(pendingCSRSchema).default([]),
});

export type PendingCSR = z.infer<typeof pendingCSRSchema>;

/**
 * The floor the server enforces on every recorded acknowledgement. Mirrored so the button disables
 * rather than the operator losing an upload to a 422.
 */
export const MIN_ACKNOWLEDGEMENT = 20;

export const acknowledgementSchema = z
  .string()
  .trim()
  .min(MIN_ACKNOWLEDGEMENT, `At least ${MIN_ACKNOWLEDGEMENT} characters.`);

/** The customer's own change record. Bounded to match the server rather than truncated silently. */
export const importReferenceSchema = z.string().trim().max(200);

/**
 * The material step's input: one file, one password.
 *
 * ── ONE PKCS#12 REPLACED THREE FIELDS ───────────────────────────────────────
 *
 * What a handover actually contains is one file. A customer's PKI team exports the authority they
 * issued for PureID as a .pfx — the certificate, the chain above it and the private key in one
 * container, under one password — and hands that over. The console used to take it apart again: a
 * certificate field, a chain field, and then the same .pfx one step later as "the key". Three inputs
 * for one file, and the commonest failure was the obvious one: an operator put the .pfx in the
 * certificate field and was told the certificate "could not be read".
 *
 * ── THE CHAIN IS NOT A FIELD, BECAUSE IT IS INSIDE THE FILE ─────────────────
 *
 * A .pfx exported with its certification path carries the intermediates and the root, and the server
 * sorts them. One exported without the path carries none, and the server's `chain` check refuses and
 * names the fix — re-export including the path. That fix belongs to the exporting side, so offering a
 * separate chain upload here would invite an operator to paper over a bad handover with a second file
 * whose relationship to the first nobody checked.
 *
 * ── THE FILE IS PART OF THE VALUE, AND IT IS NEVER READ HERE ────────────────
 *
 * Validation confirms a file was chosen and a password typed; the bytes are read inside the mutation
 * functions, once per request, and are not held anywhere this schema or any cache can see them
 * (contract §4).
 */
export const importMaterialInputSchema = z.object({
  file: z.instanceof(File, { message: "Choose the .pfx file your PKI team exported." }),
  password: z.string().min(1, "Enter the password that protects this file."),
});

export type ImportMaterialInput = z.infer<typeof importMaterialInputSchema>;

export type CertificateProfile = z.infer<typeof certificateProfileSchema>;
export type ProfileVersion = z.infer<typeof profileVersionSchema>;
export type ProfileWindow = z.infer<typeof profileWindowSchema>;
export type LiveProfile = z.infer<typeof liveProfileSchema>;

/**
 * Hours as an operator holds them.
 *
 * A CEILING IS READ IN DAYS ONCE IT PASSES ONE, because the decision it encodes is "does a laptop
 * survive a weekend" and 72 is a number people have to divide. Below a day it stays in hours, where
 * the same decision is "does someone reach a coffee shop".
 */
export function formatWindowHours(hours: number): string {
  if (hours <= 0) return "none";
  if (hours < 1) return formatWindowMinutes(Math.round(hours * 60));
  if (hours < 24) return hours === 1 ? "1 hour" : `${formatNumber(hours)} hours`;
  const days = hours / 24;
  return days === 1 ? "1 day" : `${formatNumber(days)} days`;
}

/**
 * The same ladder, entered a rung lower — and it does NOT round the minutes away.
 *
 * WHOLE UNITS GO UP THE LADDER; A REMAINDER STAYS VISIBLE. Handing everything at or above an hour to
 * formatWindowHours was the first version of this, and it defeated the reason the pair is in minutes
 * at all: that function divides and prints one decimal, so 61, 62 and 65 minutes all render as "1.0
 * hours" or "1.1 hours" — indistinguishable from the 60 sitting beside them on the same card. The doc
 * comment on the wire shape argues a rounded number is a claim rather than a rounding; this is where
 * that argument is either kept or quietly abandoned.
 *
 * So an exact hour reads as an hour, an exact day as a day, and anything in between says both parts.
 */
export function formatWindowMinutes(minutes: number): string {
  if (minutes <= 0) return "none";
  if (minutes < 60) return plural(minutes, "minute");
  if (minutes % 60 !== 0) {
    // Hours-and-minutes even past a day: days-and-hours would drop the minutes entirely, and losing
    // the part that made this field worth adding is the failure, not the long string.
    return `${plural(Math.floor(minutes / 60), "hour")} ${plural(minutes % 60, "minute")}`;
  }
  const hours = minutes / 60;
  if (hours % 24 === 0) return plural(hours / 24, "day");
  // A WHOLE NUMBER OF HOURS PAST A DAY COMPOUNDS TOO. Leaving it in hours is exact and unreadable —
  // a 900-hour window rendered "900 hours" on a real screen, which nobody divides by 24 in their
  // head. Same rule as the line above, one unit up.
  if (hours > 24) return `${plural(Math.floor(hours / 24), "day")} ${plural(hours % 24, "hour")}`;
  return plural(hours, "hour");
}

function plural(value: number, unit: string): string {
  return value === 1 ? `1 ${unit}` : `${formatNumber(value)} ${unit}s`;
}

function formatNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}
